import { getAmplifyClient } from '../amplifyClient';
import { getYstnjUiConsultationServiceStatus } from '../settings/directRuntimeQueries';
import { getYstnjPaymentControlPlaneReadiness } from '../settings/directRuntimeQueries';
import { getRuntimeAmplifyOutputs } from '../auth/oauthRuntimeConfig';
import type {
  AcademicsMutationArgsMap,
  AcademicsMutationResultMap,
  AcademicsQueryArgsMap,
  AcademicsQueryResultMap,
} from '@triedge/academics-domain';
import type {
  AdminGovernanceMutationArgsMap,
  AdminGovernanceMutationResultMap,
  AdminGovernanceQueryArgsMap,
  AdminGovernanceQueryResultMap,
  AssistantIntegrationsMutationArgsMap,
  AssistantIntegrationsMutationResultMap,
  AssistantIntegrationsQueryArgsMap,
  AssistantIntegrationsQueryResultMap,
  AssistantPlatformMutationArgsMap,
  AssistantPlatformMutationResultMap,
  AssistantPlatformQueryArgsMap,
  AssistantPlatformQueryResultMap,
  CampsMutationArgsMap,
  CampsMutationResultMap,
  CampsQueryArgsMap,
  CampsQueryResultMap,
  DomainRouterConnection,
  DomainRouterTransportEnvelope,
} from '@triedge/domain-core';
import {
  serializeDomainRouterTransportInput,
} from '@triedge/domain-core';
import type {
  DomainRouterKnownMutationName,
  DomainRouterKnownQueryName,
} from '../state/domainRouterOperationRegistry';
import type {
  AdmissionsMutationArgsMap,
  AdmissionsMutationResultMap,
  AdmissionsQueryArgsMap,
  AdmissionsQueryResultMap,
} from '@triedge/admissions-domain';
import type {
  AttendanceMutationArgsMap,
  AttendanceMutationResultMap,
  AttendanceQueryArgsMap,
  AttendanceQueryResultMap,
} from '@triedge/attendance-domain/router-contract';
import type {
  FacultyMutationArgsMap,
  FacultyMutationResultMap,
  FacultyQueryArgsMap,
  FacultyQueryResultMap,
} from '@triedge/faculty-domain';
import type {
  FinancialAidMutationArgsMap,
  FinancialAidMutationResultMap,
  FinancialAidQueryArgsMap,
  FinancialAidQueryResultMap,
} from '@triedge/financial-aid-domain';
import type {
  MessagingMutationArgsMap,
  MessagingMutationResultMap,
  MessagingQueryArgsMap,
  MessagingQueryResultMap,
  ParentPortalQueryArgsMap,
  ParentPortalQueryResultMap,
} from '@triedge/parent-portal-domain';
import type {
  PayrollMutationArgsMap,
  PayrollMutationResultMap,
  PayrollQueryArgsMap,
  PayrollQueryResultMap,
} from '@triedge/payroll-domain';
import type {
  DonationsMutationArgsMap,
  DonationsMutationResultMap,
  DonationsQueryArgsMap,
  DonationsQueryResultMap,
  NotificationsMutationArgsMap,
  NotificationsMutationResultMap,
  NotificationsQueryArgsMap,
  NotificationsQueryResultMap,
  PaymentsMutationArgsMap,
  PaymentsMutationResultMap,
  PaymentsQueryArgsMap,
  PaymentsQueryResultMap,
} from '@triedge/domain-core';
import type {
  StaffSettingsMutationArgsMap,
  StaffSettingsMutationResultMap,
  StaffSettingsQueryArgsMap,
  StaffSettingsQueryResultMap,
} from '@triedge/staff-settings-domain';
import type {
  StudentsFamiliesMutationArgsMap,
  StudentsFamiliesMutationResultMap,
  StudentsFamiliesQueryArgsMap,
  StudentsFamiliesQueryResultMap,
} from '@triedge/students-families-domain';
import {
  resolveRoutedMutationTransport,
  resolveRoutedQueryTransport,
  type RoutedTransportTarget,
} from './domainRouterTransportRegistry';
import {
  ystnjKnownAwsJsonFieldShapes,
} from '@triedge/rtk-abstractions';

type GraphQLErrorLike = {
  message?: string;
};

type GraphqlResult<TData> = {
  data: TData | null;
  errors?: GraphQLErrorLike[] | null;
};

type AmplifyGraphqlClient = {
  graphql: (request: {
    query: string;
    variables?: Record<string, unknown>;
    authMode?: string;
  }) => Promise<GraphqlResult<Record<string, RoutedTransportPayload>>>;
};

type AmplifyClient = ReturnType<typeof getAmplifyClient>;
type QueryMap = AmplifyClient extends { queries: infer Queries } ? Queries : never;
type MutationMap = AmplifyClient extends { mutations: infer Mutations } ? Mutations : never;
type FallbackConnectionItem = Record<string, unknown>;
type FallbackQueryData = Record<string, unknown> & {
  items?: readonly FallbackConnectionItem[] | FallbackConnectionItem[] | null;
  totalCount?: number | null;
  nextToken?: string | null;
};
type FallbackMutationData = Record<string, unknown>;
type FallbackQueryFn = (
  args?: Record<string, unknown>
) => Promise<GraphqlResult<FallbackQueryData>>;
type FallbackMutationFn = (
  args: { input: unknown }
) => Promise<GraphqlResult<FallbackMutationData>>;
type QueryFnFor<Name extends DomainRouterQueryName> = Name extends keyof QueryMap
  ? QueryMap[Name]
  : FallbackQueryFn;
type MutationFnFor<Name extends DomainRouterMutationName> = Name extends keyof MutationMap
  ? MutationMap[Name]
  : FallbackMutationFn;
type OperationArg<TFn> = TFn extends (...args: infer TArgs) => unknown
  ? TArgs extends []
    ? void
    : TArgs[0]
  : void;
type ExpandVoidArg<TArg> = [TArg] extends [void] ? Record<string, unknown> | void : TArg;

export type DomainRouterQueryName = DomainRouterKnownQueryName;
export type DomainRouterMutationName = DomainRouterKnownMutationName;
type RoutedDomainQueryArgsMap =
  & AdminGovernanceQueryArgsMap
  & AcademicsQueryArgsMap
  & AssistantIntegrationsQueryArgsMap
  & AssistantPlatformQueryArgsMap
  & CampsQueryArgsMap
  & AdmissionsQueryArgsMap
  & AttendanceQueryArgsMap
  & DonationsQueryArgsMap
  & FacultyQueryArgsMap
  & FinancialAidQueryArgsMap
  & MessagingQueryArgsMap
  & NotificationsQueryArgsMap
  & PayrollQueryArgsMap
  & ParentPortalQueryArgsMap
  & PaymentsQueryArgsMap
  & StaffSettingsQueryArgsMap
  & StudentsFamiliesQueryArgsMap;
type RoutedDomainMutationArgsMap =
  & AdminGovernanceMutationArgsMap
  & AcademicsMutationArgsMap
  & AssistantIntegrationsMutationArgsMap
  & AssistantPlatformMutationArgsMap
  & CampsMutationArgsMap
  & AdmissionsMutationArgsMap
  & AttendanceMutationArgsMap
  & DonationsMutationArgsMap
  & FacultyMutationArgsMap
  & FinancialAidMutationArgsMap
  & MessagingMutationArgsMap
  & NotificationsMutationArgsMap
  & PayrollMutationArgsMap
  & PaymentsMutationArgsMap
  & StaffSettingsMutationArgsMap
  & StudentsFamiliesMutationArgsMap;
type RoutedDomainQueryResultMap =
  & AdminGovernanceQueryResultMap
  & AcademicsQueryResultMap
  & AssistantIntegrationsQueryResultMap
  & AssistantPlatformQueryResultMap
  & CampsQueryResultMap
  & AdmissionsQueryResultMap
  & AttendanceQueryResultMap
  & DonationsQueryResultMap
  & FacultyQueryResultMap
  & FinancialAidQueryResultMap
  & MessagingQueryResultMap
  & NotificationsQueryResultMap
  & PayrollQueryResultMap
  & ParentPortalQueryResultMap
  & PaymentsQueryResultMap
  & StaffSettingsQueryResultMap
  & StudentsFamiliesQueryResultMap;
type RoutedDomainMutationResultMap =
  & AdminGovernanceMutationResultMap
  & AcademicsMutationResultMap
  & AssistantIntegrationsMutationResultMap
  & AssistantPlatformMutationResultMap
  & CampsMutationResultMap
  & AdmissionsMutationResultMap
  & AttendanceMutationResultMap
  & DonationsMutationResultMap
  & FacultyMutationResultMap
  & FinancialAidMutationResultMap
  & MessagingMutationResultMap
  & NotificationsMutationResultMap
  & PayrollMutationResultMap
  & PaymentsMutationResultMap
  & StaffSettingsMutationResultMap
  & StudentsFamiliesMutationResultMap;

type RoutedDomainQueryName = keyof RoutedDomainQueryArgsMap & DomainRouterQueryName;
type RoutedDomainMutationName = keyof RoutedDomainMutationArgsMap & DomainRouterMutationName;

export type DomainRouterQueryArgs<Name extends DomainRouterQueryName> =
  Name extends RoutedDomainQueryName
    ? RoutedDomainQueryArgsMap[Name]
    : ExpandVoidArg<OperationArg<QueryFnFor<Name>>>;
export type DomainRouterMutationArgs<Name extends DomainRouterMutationName> =
  Name extends RoutedDomainMutationName
    ? RoutedDomainMutationArgsMap[Name]
    : ExpandVoidArg<OperationArg<MutationFnFor<Name>>>;
export type DomainRouterQueryResult<Name extends DomainRouterQueryName> = Awaited<
  ReturnType<QueryFnFor<Name>>
>;
export type DomainRouterMutationResult<Name extends DomainRouterMutationName> = Awaited<
  ReturnType<MutationFnFor<Name>>
>;
export type DomainRouterQueryData<Name extends DomainRouterQueryName> =
  Name extends RoutedDomainQueryName
    ? RoutedDomainQueryResultMap[Name]
    : DomainRouterQueryResult<Name> extends GraphqlResult<infer TData> ? TData : never;
export type DomainRouterMutationData<Name extends DomainRouterMutationName> =
  Name extends RoutedDomainMutationName
    ? RoutedDomainMutationResultMap[Name]
    : DomainRouterMutationResult<Name> extends GraphqlResult<infer TData> ? TData : never;

type AwsJsonShape = 'object' | 'array';
type JsonRecord = Record<string, unknown>;
type AwsJsonFieldIndex = Readonly<Record<string, readonly string[]>>;

const ASSISTANT_AWSJSON_OPERATION_FIELDS: Readonly<Record<string, Readonly<Record<string, AwsJsonShape>>>> =
  ystnjKnownAwsJsonFieldShapes;

const EMPTY_AWSJSON_FIELDS: readonly string[] = [];

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const collectAwsJsonFields = (
  operationMap: unknown,
  index: Map<string, Set<string>>
): void => {
  if (!isRecord(operationMap)) {
    return;
  }
  for (const [operationName, operationDefinition] of Object.entries(operationMap)) {
    if (!isRecord(operationDefinition)) {
      continue;
    }
    const argumentsDefinition = operationDefinition.arguments;
    if (!isRecord(argumentsDefinition)) {
      continue;
    }
    for (const [argumentName, argumentDefinition] of Object.entries(argumentsDefinition)) {
      if (!isRecord(argumentDefinition) || argumentDefinition.type !== 'AWSJSON') {
        continue;
      }
      const fieldSet = index.get(operationName) ?? new Set<string>();
      fieldSet.add(argumentName);
      index.set(operationName, fieldSet);
    }
  }
};

const buildAwsJsonFieldIndex = (): AwsJsonFieldIndex => {
  const index = new Map<string, Set<string>>();
  const outputs = getRuntimeAmplifyOutputs();
  const data = isRecord(outputs.data) ? outputs.data : null;
  const modelIntrospection = data && isRecord(data.model_introspection)
    ? data.model_introspection
    : null;
  if (!modelIntrospection) {
    return {};
  }
  collectAwsJsonFields(modelIntrospection.queries, index);
  collectAwsJsonFields(modelIntrospection.mutations, index);
  return Object.fromEntries(
    Array.from(index.entries(), ([operationName, fields]) => [
      operationName,
      Object.freeze(Array.from(fields).sort()),
    ])
  );
};

const AWSJSON_FIELDS_BY_OPERATION = buildAwsJsonFieldIndex();

const sanitizeAwsJsonValue = (value: unknown): unknown => {
  const visited = new WeakSet<object>();
  const serialized = JSON.stringify(value, (_key, candidate) => {
    if (typeof candidate === 'bigint') {
      return candidate.toString();
    }
    if (typeof candidate === 'number' && !Number.isFinite(candidate)) {
      return null;
    }
    if (typeof candidate === 'function' || typeof candidate === 'symbol') {
      return undefined;
    }
    if (candidate && typeof candidate === 'object') {
      if (visited.has(candidate as object)) {
        return undefined;
      }
      visited.add(candidate as object);
    }
    return candidate;
  });

  if (typeof serialized !== 'string') {
    return null;
  }
  try {
    return JSON.parse(serialized);
  } catch {
    return null;
  }
};

const sanitizeAwsJsonObject = (value: unknown): Record<string, unknown> => {
  const normalized = sanitizeAwsJsonValue(value);
  if (!normalized || typeof normalized !== 'object' || Array.isArray(normalized)) {
    return {};
  }
  return normalized as Record<string, unknown>;
};

const sanitizeAwsJsonArray = (value: unknown): unknown[] => {
  const normalized = sanitizeAwsJsonValue(value);
  return Array.isArray(normalized) ? normalized : [];
};

const parseAwsJsonCandidate = (candidate: unknown, shape: AwsJsonShape): unknown => {
  if (typeof candidate !== 'string') {
    return candidate;
  }
  try {
    return JSON.parse(candidate);
  } catch {
    return shape === 'array' ? [] : {};
  }
};

const coerceAwsJsonValue = (value: unknown, shape: AwsJsonShape): unknown => {
  const parsed = parseAwsJsonCandidate(value, shape);
  return shape === 'array'
    ? sanitizeAwsJsonArray(parsed)
    : sanitizeAwsJsonObject(parsed);
};

const coerceAwsJsonString = (value: unknown, shape: AwsJsonShape): string =>
  JSON.stringify(coerceAwsJsonValue(value, shape));

const coerceGenericAwsJsonString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(sanitizeAwsJsonValue(value));
};

const stripNullishArgs = <TArgs extends Record<string, unknown>>(args: TArgs): TArgs => {
  const filteredEntries = Object.entries(args).filter(([, value]) => value != null);
  if (filteredEntries.length === Object.keys(args).length) {
    return args;
  }
  return Object.fromEntries(filteredEntries) as TArgs;
};

export const normalizeAssistantAwsJsonOperationArgs = (
  operationName: string,
  args: Record<string, unknown>
): Record<string, unknown> => {
  const fieldShapeMap = ASSISTANT_AWSJSON_OPERATION_FIELDS[operationName];
  const awsJsonFields = AWSJSON_FIELDS_BY_OPERATION[operationName] ?? EMPTY_AWSJSON_FIELDS;
  if (!fieldShapeMap && awsJsonFields.length === 0) {
    return args;
  }

  const fieldNames = new Set<string>(awsJsonFields);
  if (fieldShapeMap) {
    for (const fieldName of Object.keys(fieldShapeMap)) {
      fieldNames.add(fieldName);
    }
  }

  let normalizedArgs: Record<string, unknown> | null = null;
  for (const fieldName of fieldNames) {
    if (!Object.prototype.hasOwnProperty.call(args, fieldName)) {
      continue;
    }
    const currentValue = args[fieldName];
    if (currentValue == null) {
      continue;
    }
    const shape = fieldShapeMap?.[fieldName];
    const normalizedValue = shape
      ? coerceAwsJsonString(currentValue, shape)
      : coerceGenericAwsJsonString(currentValue);
    if (normalizedValue === currentValue) {
      continue;
    }
    if (!normalizedArgs) {
      normalizedArgs = { ...args };
    }
    normalizedArgs[fieldName] = normalizedValue;
  }

  return stripNullishArgs(normalizedArgs ?? args);
};

export const normalizeAssistantAwsJsonMutationArgs = normalizeAssistantAwsJsonOperationArgs;
export const normalizeAssistantAwsJsonQueryArgs = normalizeAssistantAwsJsonOperationArgs;

const formatErrors = (errors: GraphQLErrorLike[] | null | undefined): string =>
  (errors ?? [])
    .map((entry) => String(entry?.message ?? '').trim())
    .filter(Boolean)
    .join('; ');

const parseTimeoutMs = (raw: unknown, fallback: number): number => {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? Math.round(value) : fallback;
};

const DOMAIN_ROUTER_TIMEOUT_FALLBACK_MS = 15000;
const DOMAIN_ROUTER_TIMEOUT_OVERRIDES_MS: Readonly<Record<string, number>> = {
  'mutation:askSchoolAI': 30000,
  'mutation:askAdmissionsAI': 30000,
  'mutation:generateFinancialAidApplicationReport': 120000,
};

export const getDomainRouterOperationTimeoutMs = (label: string): number =>
  DOMAIN_ROUTER_TIMEOUT_OVERRIDES_MS[label] ?? DOMAIN_ROUTER_TIMEOUT_MS;

declare global {
  interface Window {
    __TRIEDGE_DOMAIN_ROUTER_TIMEOUT_MS__?: number | string;
  }
}

const readDomainRouterTimeoutMs = (): number => {
  if (typeof window === 'undefined') {
    return DOMAIN_ROUTER_TIMEOUT_FALLBACK_MS;
  }
  return parseTimeoutMs(
    window.__TRIEDGE_DOMAIN_ROUTER_TIMEOUT_MS__,
    DOMAIN_ROUTER_TIMEOUT_FALLBACK_MS
  );
};

const DOMAIN_ROUTER_TIMEOUT_MS = readDomainRouterTimeoutMs();

const withOperationTimeout = async <T>(label: string, promise: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutMs = getDomainRouterOperationTimeoutMs(label);
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`${label} timed out after ${timeoutMs}ms.`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const unwrapResult = <TData>(operationLabel: string, result: GraphqlResult<TData>): TData => {
  const errorMessage = formatErrors(result.errors);
  if (errorMessage) {
    throw new Error(`${operationLabel} failed: ${errorMessage}`);
  }
  if (result.data == null) {
    throw new Error(`${operationLabel} returned no data.`);
  }
  return result.data;
};

export const resolveDomainRouterQueryTransport = (
  name: DomainRouterQueryName
): RoutedTransportTarget | undefined => resolveRoutedQueryTransport(name);

export const resolveDomainRouterMutationTransport = (
  name: DomainRouterMutationName
): RoutedTransportTarget | undefined => resolveRoutedMutationTransport(name);

type RoutedTransportPayload = DomainRouterTransportEnvelope<unknown> & {
  payload?: unknown;
};

const buildTransportDocument = (
  operationKind: 'query' | 'mutation',
  fieldName: string
): string => /* GraphQL */ `
  ${operationKind} DomainRouterTransport($operationId: String!, $input: AWSJSON) {
    ${fieldName}(operationId: $operationId, input: $input) {
      operationId
      payload
    }
  }
`;

const parseJsonPayloadCandidate = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const unwrapRoutedPayload = <TData>(
  operationLabel: string,
  envelope: RoutedTransportPayload | null | undefined
): TData => {
  if (!envelope || typeof envelope !== 'object') {
    throw new Error(`${operationLabel} returned no router envelope.`);
  }
  return parseJsonPayloadCandidate(envelope.payload) as TData;
};

const collectErrorMessages = (
  value: unknown,
  messages: string[] = [],
  seen = new WeakSet<object>(),
): string[] => {
  if (value == null) {
    return messages;
  }
  if (typeof value === 'string') {
    messages.push(value);
    try {
      collectErrorMessages(JSON.parse(value), messages, seen);
    } catch {
      // Not JSON; the raw string is already recorded.
    }
    return messages;
  }
  if (value instanceof Error) {
    messages.push(value.message);
  }
  if (typeof value !== 'object') {
    messages.push(String(value));
    return messages;
  }
  if (seen.has(value)) {
    return messages;
  }
  seen.add(value);
  const record = value as Record<string, unknown>;
  ['message', 'errorMessage', 'reason', 'details'].forEach((key) => {
    collectErrorMessages(record[key], messages, seen);
  });
  ['errors', 'graphQLErrors'].forEach((key) => {
    const nested = record[key];
    if (Array.isArray(nested)) {
      nested.forEach((entry) => collectErrorMessages(entry, messages, seen));
    } else {
      collectErrorMessages(nested, messages, seen);
    }
  });
  return messages;
};

const isUnsupportedRunReportsWorkspaceAggregateError = (error: unknown): boolean => {
  const message = collectErrorMessages(error).join('\n');
  return /unsupported (?:field|operation) getAssistantReportGenerationWorkspace/i.test(message);
};

const parseMaybeJsonRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
};

const unwrapWorkspaceAggregateInput = (args: unknown): Record<string, unknown> => {
  const record = parseMaybeJsonRecord(args);
  return Object.prototype.hasOwnProperty.call(record, 'input')
    ? parseMaybeJsonRecord(record.input)
    : record;
};

const positiveLimit = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
};

const boundedPositiveLimit = (value: unknown, fallback: number, max: number): number =>
  Math.min(positiveLimit(value, fallback), max);

const stringOrUndefined = (value: unknown): string | undefined => {
  const normalized = String(value ?? '').trim();
  return normalized || undefined;
};

const REPORT_GENERATION_WORKFLOW_PREVIEW_LIMIT_MAX = 200;

const composeRunReportsWorkspaceFromLiveOperations = async (
  args: DomainRouterQueryArgs<'getAssistantReportGenerationWorkspace'>,
): Promise<DomainRouterQueryData<'getAssistantReportGenerationWorkspace'>> => {
  const input = unwrapWorkspaceAggregateInput(args);
  const pathname = stringOrUndefined(input.pathname) ?? '/school/run-reports';
  const module = input.module ?? 'tuition';
  const reportDefinition = input.reportDefinition;
  const previewLimit = boundedPositiveLimit(
    input.previewLimit,
    50,
    REPORT_GENERATION_WORKFLOW_PREVIEW_LIMIT_MAX,
  );
  const templateLimit = positiveLimit(input.templateLimit, 100);
  const scheduleLimit = positiveLimit(input.scheduleLimit, 100);
  const workItemLimit = positiveLimit(input.workItemLimit, 100);
  const [preview, templates, schedules, history] = await Promise.all([
    domainRouterClient.query('previewAssistantReportGeneration', {
      input: {
        pathname,
        module,
        reportDefinition,
        limit: previewLimit,
      },
    } as DomainRouterQueryArgs<'previewAssistantReportGeneration'>),
    domainRouterClient.query('listAssistantReportTemplates', {
      input: {
        pathname,
        includeShared: input.includeSharedTemplates ?? true,
        includeArchived: input.includeArchivedTemplates ?? false,
        limit: templateLimit,
        nextToken: stringOrUndefined(input.templatesNextToken),
      },
    } as DomainRouterQueryArgs<'listAssistantReportTemplates'>),
    domainRouterClient.query('listAssistantReportSchedules', {
      input: {
        includeDisabled: input.includeDisabledSchedules ?? true,
        limit: scheduleLimit,
        nextToken: stringOrUndefined(input.schedulesNextToken),
      },
    } as DomainRouterQueryArgs<'listAssistantReportSchedules'>),
    domainRouterClient.query('listAssistantWorkItems', {
      input: {
        includeClosed: input.includeClosedWorkItems ?? true,
        module,
        pathname,
        limit: workItemLimit,
        nextToken: stringOrUndefined(input.historyNextToken),
      },
    } as DomainRouterQueryArgs<'listAssistantWorkItems'>),
  ]);

  return {
    preview,
    templates,
    schedules,
    history,
    metrics: {
      queryShapeVersion: 'run-reports-workspace-v1',
      appSyncOperationBudget: 4,
      serverSideOperationBudget: 4,
      previewLimit,
      templateLimit,
      scheduleLimit,
      workItemLimit,
    },
  };
};

export const normalizeRoutedTransportMutationArgs = (args: unknown): unknown => {
  if (!args || typeof args !== 'object' || Array.isArray(args)) {
    return args;
  }
  const record = args as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== 1 || keys[0] !== 'input') {
    return args;
  }
  return record.input;
};

export const normalizeRoutedTransportQueryArgs = (args: unknown): unknown => {
  if (!args || typeof args !== 'object' || Array.isArray(args)) {
    return args;
  }
  const record = args as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length !== 1 || keys[0] !== 'input') {
    return args;
  }
  return record.input;
};

const asGraphqlClient = (client: unknown): AmplifyGraphqlClient => {
  const candidate = client as AmplifyGraphqlClient;
  if (typeof candidate.graphql !== 'function') {
    throw new Error('Amplify client does not expose graphql() for routed transport execution.');
  }
  return candidate;
};

const executeRoutedTransport = async <TData>(
  input: {
    operationKind: 'query' | 'mutation';
    transportField: string;
    operationLabel: string;
    operationId: string;
    normalizedArgs: unknown;
  }
): Promise<TData> => {
  const client = asGraphqlClient(getAmplifyClient());
  const result = await withOperationTimeout(
    input.operationLabel,
    client.graphql({
      query: buildTransportDocument(input.operationKind, input.transportField),
      variables: {
        operationId: input.operationId,
        input: serializeDomainRouterTransportInput(input.normalizedArgs),
      },
    })
  );
  const data = unwrapResult(
    input.operationLabel,
    result as GraphqlResult<Record<string, RoutedTransportPayload>>
  );
  return unwrapRoutedPayload<TData>(
    input.operationLabel,
    data[input.transportField]
  );
};

export const domainRouterClient = {
  queries: () => getAmplifyClient().queries,
  mutations: () => getAmplifyClient().mutations,

  async query<Name extends DomainRouterQueryName>(
    name: Name,
    args: DomainRouterQueryArgs<Name>
  ): Promise<DomainRouterQueryData<Name>> {
    const operationName = String(name);
    const normalizedArgs = normalizeAssistantAwsJsonQueryArgs(
      operationName,
      (args ?? {}) as Record<string, unknown>
    ) as DomainRouterQueryArgs<Name>;
    if (operationName === 'getPaymentControlPlaneReadiness') {
      return getYstnjPaymentControlPlaneReadiness(
        normalizedArgs as Record<string, unknown>,
      ) as Promise<DomainRouterQueryData<Name>>;
    }
    if (operationName === 'getUiConsultationServiceStatus') {
      return getYstnjUiConsultationServiceStatus() as Promise<DomainRouterQueryData<Name>>;
    }
    const routedQueryTransport = resolveRoutedQueryTransport(name);
    if (routedQueryTransport) {
      try {
        return await executeRoutedTransport<DomainRouterQueryData<Name>>({
          operationKind: 'query',
          transportField: routedQueryTransport.transportField,
          operationLabel: `query:${operationName}`,
          operationId: operationName,
          normalizedArgs: normalizeRoutedTransportQueryArgs(
            (normalizedArgs ?? {}) as Record<string, unknown>
          ),
        });
      } catch (error) {
        if (
          operationName === 'getAssistantReportGenerationWorkspace'
          && isUnsupportedRunReportsWorkspaceAggregateError(error)
        ) {
          return composeRunReportsWorkspaceFromLiveOperations(
            normalizedArgs as DomainRouterQueryArgs<'getAssistantReportGenerationWorkspace'>,
          ) as Promise<DomainRouterQueryData<Name>>;
        }
        throw error;
      }
    }
    const queryOperations = getAmplifyClient().queries as Record<string, unknown>;
    const queryCandidate = queryOperations[operationName];
    if (typeof queryCandidate !== 'function') {
      throw new Error(
        `query:${operationName} is unavailable in the current Amplify outputs manifest. `
        + 'Regenerate apps/ystnj/amplify_outputs.json from the target branch backend deploy '
        + '(for example, via `pnpm --dir apps/ystnj exec ampx pipeline-deploy --branch <branch> --app-id <app-id> --outputs-out-dir . --outputs-format json`).'
      );
    }
    const query = queryCandidate as (
      input: DomainRouterQueryArgs<Name>,
      options?: unknown
    ) => Promise<DomainRouterQueryResult<Name>>;
    const result = await withOperationTimeout(`query:${operationName}`, query(normalizedArgs));
    return unwrapResult(`query:${operationName}`, result as GraphqlResult<DomainRouterQueryData<Name>>);
  },

  async mutate<Name extends DomainRouterMutationName>(
    name: Name,
    args: DomainRouterMutationArgs<Name>
  ): Promise<DomainRouterMutationData<Name>> {
    const operationName = String(name);
    const routedMutationTransport = resolveRoutedMutationTransport(name);
    const normalizedArgs = normalizeAssistantAwsJsonMutationArgs(
      operationName,
      (args ?? {}) as Record<string, unknown>
    ) as DomainRouterMutationArgs<Name>;
    if (routedMutationTransport) {
      return executeRoutedTransport<DomainRouterMutationData<Name>>({
        operationKind: 'mutation',
        transportField: routedMutationTransport.transportField,
        operationLabel: `mutation:${operationName}`,
        operationId: operationName,
        normalizedArgs: normalizeRoutedTransportMutationArgs(normalizedArgs ?? {}),
      });
    }
    const mutationOperations = getAmplifyClient().mutations as Record<string, unknown>;
    const mutationCandidate = mutationOperations[operationName];
    if (typeof mutationCandidate !== 'function') {
      throw new Error(
        `mutation:${operationName} is unavailable in the current Amplify outputs manifest. `
        + 'Regenerate apps/ystnj/amplify_outputs.json from the target branch backend deploy '
        + '(for example, via `pnpm --dir apps/ystnj exec ampx pipeline-deploy --branch <branch> --app-id <app-id> --outputs-out-dir . --outputs-format json`).'
      );
    }
    const mutation = mutationCandidate as (
      input: DomainRouterMutationArgs<Name>,
      options?: unknown
    ) => Promise<DomainRouterMutationResult<Name>>;
    const result = await withOperationTimeout(`mutation:${operationName}`, mutation(normalizedArgs));
    return unwrapResult(
      `mutation:${operationName}`,
      result as GraphqlResult<DomainRouterMutationData<Name>>
    );
  },
};
